import { useCallback, useMemo } from "react";
import { TOption } from "type";
import { CategoryListItem } from "features/mst_proc_fee/types/proc-fee";

/** Checkbox selection aligned with makerList / categoryList indices */
export interface ProcFeeFilterSelection {
  selectedMaker: boolean[];
  selectedCategory: boolean[];
  /** 店舗 在庫 — client-side filter, OR'd with stockFilterDepotVendor. Optional to keep legacy call sites (server-side query builders) unchanged. */
  stockFilterStore?: boolean;
  /** 倉庫/仕入先 在庫 — client-side filter, OR'd with stockFilterStore. Optional to keep legacy call sites (server-side query builders) unchanged. */
  stockFilterDepotVendor?: boolean;
}

/** Query fields for tire-set API (getItemDetailTireSetApi) */
export interface ProcFeeFilterQueryParams {
  maker_names: string;
  categories: string;
}

/**
 * Map maker/category lists + checkbox state to API query params.
 * Single source of truth for SetTireProcFeeList and WheelTireProcFeeList.
 */
export function buildProcFeeFilterQueryParams(
  makerList: TOption[],
  categoryList: CategoryListItem[],
  selection: ProcFeeFilterSelection,
): ProcFeeFilterQueryParams {
  return {
    maker_names: makerList
      .filter((_, index) => selection.selectedMaker[index])
      .map((item) => item.value)
      .join(","),
    categories: categoryList
      .filter((_, index) => selection.selectedCategory[index])
      .map((el) => el.category)
      .join(","),
  };
}

/** Modal passes overrides on Apply; otherwise use current committed selection */
export function resolveProcFeeFilterSelection(
  overrides: ProcFeeFilterSelection | undefined,
  current: ProcFeeFilterSelection,
): ProcFeeFilterSelection {
  if (!overrides) return current;
  return {
    selectedMaker: overrides.selectedMaker,
    selectedCategory: overrides.selectedCategory,
    stockFilterStore: overrides.stockFilterStore,
    stockFilterDepotVendor: overrides.stockFilterDepotVendor,
  };
}

/** Default: all makers on, no categories, stock filters off (matches first-load behavior) */
export function getDefaultProcFeeFilterSelection(
  makerList: TOption[],
  categoryList: CategoryListItem[],
): ProcFeeFilterSelection {
  return {
    selectedMaker: makerList.map(() => true),
    selectedCategory: categoryList.map(() => false),
    stockFilterStore: false,
    stockFilterDepotVendor: false,
  };
}

export interface UseProcFeeFilterOptions {
  makerList: TOption[];
  categoryList: CategoryListItem[];
  selectedMaker: boolean[];
  selectedCategory: boolean[];
  setSelectedMaker: (value: boolean[]) => void;
  setSelectedCategory: (value: boolean[]) => void;
}

/**
 * Shared filter helpers for screens that keep selection in React state (e.g. wheel flow).
 * Set-tire flow uses a reducer; it imports buildProcFeeFilterQueryParams only.
 */
export function useProcFeeFilter({
  makerList,
  categoryList,
  selectedMaker,
  selectedCategory,
  setSelectedMaker,
  setSelectedCategory,
}: UseProcFeeFilterOptions) {
  const filter = useMemo<ProcFeeFilterSelection>(
    () => ({ selectedMaker, selectedCategory }),
    [selectedMaker, selectedCategory],
  );

  const queryParams = useMemo(
    () => buildProcFeeFilterQueryParams(makerList, categoryList, filter),
    [makerList, categoryList, filter],
  );

  const setFilter = useCallback(
    (next: ProcFeeFilterSelection) => {
      setSelectedMaker(next.selectedMaker);
      setSelectedCategory(next.selectedCategory);
    },
    [setSelectedMaker, setSelectedCategory],
  );

  const clearFilter = useCallback(() => {
    setFilter(getDefaultProcFeeFilterSelection(makerList, categoryList));
  }, [makerList, categoryList, setFilter]);

  /** When modal applies: optionally commit overrides and return resolved selection for same-tick API use */
  const applyFilter = useCallback(
    (overrides?: ProcFeeFilterSelection) => {
      const resolved = resolveProcFeeFilterSelection(overrides, filter);
      if (overrides) {
        setSelectedMaker(resolved.selectedMaker);
        setSelectedCategory(resolved.selectedCategory);
      }
      return resolved;
    },
    [filter, setSelectedMaker, setSelectedCategory],
  );

  return {
    filter,
    setFilter,
    applyFilter,
    clearFilter,
    queryParams,
  };
}
